import apiClient from "./baseService";
import type {MultiPolygon} from "geojson";
import {handleAxiosError} from "./axiosErrorHandler";
import {CropCycle, CropCyclePost} from "../types/domain/cropCycle";
import {toCropCycleClient} from "../utils/cropCycleUtils";

interface ChangeField<T> {
    old: T;
    new: T;
}

export interface FieldChangeBase {
    //common properties
    id: string;
    userId: string;
    changeDate: Date;
    companyId?: string;

    //properties that can be changed
    name: ChangeField<string>;
    soilId?: ChangeField<string>;
    irrigationTypeId?: ChangeField<string>;
    geometry: MultiPolygon | null;
    orderId?: ChangeField<string>;
    farmId?: ChangeField<string>;
    parcelId?: ChangeField<string>;
    priority?: ChangeField<number>;
    irrigationInterval?: ChangeField<number>;
    desiredMoistureLevel?: ChangeField<number>;
    irrigationCriteria?: ChangeField<number>;
    irrigationDefault?: ChangeField<number>;
    satSoilMoistBias?: ChangeField<number>;
    KSATBias?: ChangeField<number>;
    onFarmEfficiencyMin?: ChangeField<number>;
    minAppliedWater?: ChangeField<number>;
    maxAppliedWater?: ChangeField<number>;
}
export interface FieldChangeModel extends FieldChangeBase {
    cropCycles?: ChangeField<CropCyclePost[]>;
}
export interface FieldChangeModelClient extends FieldChangeBase {
    cropCycles?: ChangeField<CropCycle[]>;
}

export const getFieldChanges = async (): Promise<FieldChangeModelClient[]> => {
    return await apiClient
        .get<FieldChangeModel[]>("/field-changes/")
        .then((response) => {
            const result = response.data.map((field) => {
                const {cropCycles, ...restField} = field;
                const _field: FieldChangeModelClient = {
                    ...restField,
                    cropCycles: cropCycles
                        ? {
                              old: cropCycles.old.map(toCropCycleClient),
                              new: cropCycles.new.map(toCropCycleClient),
                          }
                        : undefined,
                };
                return _field;
            });
            return result;
        })
        .catch(handleAxiosError);
};

export const approveFieldChange = async (fieldId: string): Promise<string> => {
    return await apiClient
        .post<string>(`/field-changes/${fieldId}/approve`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const declineFieldChange = async (fieldId: string): Promise<string> => {
    return await apiClient
        .post<string>(`/field-changes/${fieldId}/decline`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};
